// File-picker helpers that work on phones as well as desktops.
//
// Two iOS Safari quirks decide whether a "load a file" button does anything
// at all, and both of them are invisible on desktop:
//
//   1. A programmatic .click() only opens the picker for an <input type="file">
//      that is actually IN the document. A detached input created with
//      document.createElement and never appended is silently ignored.
//   2. `display: none` (Tailwind's `hidden`) is enough to make the input
//      unclickable too, so hidden inputs have to be hidden the accessible way
//      — off-screen but still laid out.
//
// Everything that opens a file dialog should go through this module so those
// two rules are kept in one place.

// Accept list for lesson/deck JSON. iOS matches the MIME type rather than the
// extension in some versions, so both are listed or JSON files come back
// greyed out in the Files picker.
export const JSON_ACCEPT = '.json,application/json';

// Tailwind classes for an input that is hidden but still clickable. Use this
// instead of `hidden` on any file input a button clicks for the user.
export const HIDDEN_FILE_INPUT_CLASS = 'sr-only absolute -left-[9999px] h-0 w-0 opacity-0';

/**
 * Open a file dialog and resolve with the chosen File, or null if the user
 * backed out.
 *
 * The input is attached to the document for the lifetime of the dialog (see
 * quirk 1 above) and removed afterwards. Cancellation is detected with the
 * native `cancel` event where it exists, falling back to a window focus check
 * for older browsers — deliberately patient, because coming back from the iOS
 * Files app can take a moment and an impatient timer reports a real choice as
 * a cancel.
 *
 * @param {{ accept?: string, multiple?: boolean }} [options]
 * @returns {Promise<File | File[] | null>}
 */
export function pickFile({ accept = JSON_ACCEPT, multiple = false } = {}) {
    return new Promise((resolve) => {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = accept;
        if (multiple) input.multiple = true;
        // Hidden, but present and laid out — see quirk 2.
        input.style.cssText = 'position:fixed;left:-9999px;top:0;width:1px;height:1px;opacity:0;';
        document.body.appendChild(input);

        let settled = false;
        const finish = (value) => {
            if (settled) return;
            settled = true;
            window.removeEventListener('focus', onFocus);
            input.remove();
            resolve(value);
        };

        input.addEventListener('change', () => {
            const files = Array.from(input.files || []);
            finish(multiple ? files : (files[0] || null));
        });

        // Safari 16.4+/Chrome 113+ tell us outright when the dialog is dismissed.
        input.addEventListener('cancel', () => finish(multiple ? [] : null));

        // Fallback for browsers without `cancel`: the window regains focus both
        // when a file was chosen and when the dialog was dismissed, so give the
        // change event a generous head start before calling it a cancel.
        const onFocus = () => {
            setTimeout(() => {
                if (!input.files?.length) finish(multiple ? [] : null);
            }, 1500);
        };
        window.addEventListener('focus', onFocus);

        input.click();
    });
}

/**
 * Click a hidden file input on the user's behalf.
 *
 * Rendered inputs hit quirk 2 above, so this makes sure the element is
 * clickable before clicking it: an input hidden with `display: none` is
 * temporarily laid out off-screen for the duration of the click.
 *
 * @param {HTMLInputElement | null | undefined} input
 */
export function openFileInput(input) {
    if (!input) return;
    const hidden = getComputedStyle(input).display === 'none';
    if (!hidden) {
        input.click();
        return;
    }
    const previous = input.style.cssText;
    input.style.cssText = 'position:fixed;left:-9999px;top:0;width:1px;height:1px;opacity:0;display:block;';
    input.click();
    // Restore on the next frame — after the click has been dispatched, but
    // without leaving the input laid out for longer than necessary.
    requestAnimationFrame(() => { input.style.cssText = previous; });
}
